import React, { useState, useEffect } from 'react';
import { Card, CardTitle, Grid, MetricCard, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function fmt(n) { return n == null ? '\u2026' : n.toLocaleString(); }
function fmtUSD(n) { return n == null ? '\u2026' : '$' + Number(n).toFixed(4); }

// Claude API pricing (Haiku 4.5 — pipeline verify)
const COST_PER_1K_IN  = 0.0008;  // $0.80 / 1M input tokens
const COST_PER_1K_OUT = 0.004;   // $4.00 / 1M output tokens
const AVG_IN_RATIO    = 0.7;     // estimated input vs output split

function Skel({ h = 12, mb = 8 }) {
  return (
    <div style={{ height: h, borderRadius: 6, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

export default function APICostMonitor() {
  const [stats,   setStats]   = useState(null);
  const [daily,   setDaily]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // ADM-COST-01 (May 15, 2026) — was reading sana_invocations which is
        // empty. The live Sana log is sana_query_log with separate
        // tokens_input + tokens_output columns. Sum them for total tokens.
        const { data: rawRows } = await supabase
          .from('sana_query_log')
          .select('tokens_input,tokens_output,created_at,latency_ms')
          .order('created_at', { ascending: false })
          .limit(1000);
        const rows = (rawRows || []).map(r => ({
          tokens_used: (r.tokens_input || 0) + (r.tokens_output || 0),
          tokens_in:   r.tokens_input || 0,
          tokens_out:  r.tokens_output || 0,
          created_at:  r.created_at,
          latency_ms:  r.latency_ms,
          result_count: null,
        }));

        const today   = new Date(); today.setHours(0,0,0,0);
        const d7      = new Date(Date.now() - 7  * 86400000);
        const d30     = new Date(Date.now() - 30 * 86400000);

        let totalTokens = 0, tokensToday = 0, tokens7d = 0, tokens30d = 0;
        let totalLatency = 0, latencyCount = 0, withResults = 0;
        const byDay = {};

        (rows || []).forEach(r => {
          const t = r.tokens_used || 0;
          const ts = new Date(r.created_at);
          totalTokens += t;
          if (ts >= today)  tokensToday += t;
          if (ts >= d7)     tokens7d   += t;
          if (ts >= d30)    tokens30d  += t;
          if (r.latency_ms) { totalLatency += r.latency_ms; latencyCount++; }
          if ((r.result_count || 0) > 0) withResults++;
          // Group by day (last 14 days)
          if (ts >= new Date(Date.now() - 14 * 86400000)) {
            const dk = ts.toISOString().split('T')[0];
            byDay[dk] = (byDay[dk] || 0) + t;
          }
        });

        // Estimate cost: assume 70% input, 30% output
        const estCost = (totalTokens * AVG_IN_RATIO / 1000 * COST_PER_1K_IN)
                      + (totalTokens * (1 - AVG_IN_RATIO) / 1000 * COST_PER_1K_OUT);
        const estCost30d = (tokens30d * AVG_IN_RATIO / 1000 * COST_PER_1K_IN)
                         + (tokens30d * (1 - AVG_IN_RATIO) / 1000 * COST_PER_1K_OUT);

        const dailyArr = Object.entries(byDay)
          .sort(([a], [b]) => a.localeCompare(b))
          .slice(-14);

        if (!cancelled) {
          setStats({
            total: rows?.length || 0, totalTokens, tokensToday, tokens7d, tokens30d,
            avgLatency: latencyCount ? Math.round(totalLatency / latencyCount) : null,
            resultRate: rows?.length ? Math.round(withResults / rows.length * 100) : 0,
            estCost, estCost30d,
          });
          setDaily(dailyArr);
        }
      } catch (_e) {}
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const maxDay = daily.reduce((m, [,v]) => Math.max(m, v), 1);

  return (
    <div style={{ fontFamily: F }}>
      <style>{'@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}'}</style>

      <Grid cols={4}>
        <MetricCard label="Total Sana calls"   value={loading ? '\u2026' : fmt(stats?.total || 0)}
          sub={!loading ? (stats?.totalTokens ? fmt(stats.totalTokens) + ' tokens used' : 'token tracking not yet enabled') : undefined}
          accent="#314F5C" />
        <MetricCard label="Estimated cost"     value={loading ? '\u2026' : '$' + (stats?.estCost || 0).toFixed(2)}
          sub={!loading ? '$' + (stats?.estCost30d || 0).toFixed(2) + ' last 30d' : undefined}
          accent="#6F8FA7" />
        <MetricCard label="Avg latency"        value={loading ? '\u2026' : stats?.avgLatency ? stats.avgLatency + 'ms' : '\u2014'}
          sub="per Sana call" accent={stats?.avgLatency > 3000 ? '#e05c5c' : '#2da06a'} />
        <MetricCard label="Tokens today"       value={loading ? '\u2026' : fmt(stats?.tokensToday || 0)}
          sub={!loading ? fmt(stats?.tokens7d || 0) + ' last 7d' : undefined} accent="#AFE1F6" />
      </Grid>

      {/* 14-day token usage chart */}
      <Card>
        <CardTitle>Token usage — last 14 days</CardTitle>
        {loading ? <Skel h={80} mb={0} /> : daily.length === 0 ? (
          <Insight type="info">Token tracking is not yet enabled in the Sana API. Enable by writing tokens_used to sana_invocations on each call.</Insight>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80, marginBottom: 8 }}>
              {daily.map(([date, tokens]) => {
                const h = Math.max(4, Math.round((tokens / maxDay) * 76));
                const label = new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                return (
                  <div key={date} style={{ flex: 1, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 2 }} title={`${label}: ${fmt(tokens)} tokens`}>
                    <div style={{ width: '100%', background: '#314F5C', borderRadius: '2px 2px 0 0',
                      height: h, opacity: 0.7 }} />
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '.6rem', color: '#9eb8c8' }}>
                {daily[0] ? new Date(daily[0][0]).toLocaleDateString('en-GB', { day:'2-digit', month:'short' }) : ''}
              </span>
              <span style={{ fontSize: '.6rem', color: '#9eb8c8' }}>Today</span>
            </div>
          </>
        )}
      </Card>

      {/* Cost breakdown */}
      <Card style={{ marginTop: 0 }}>
        <CardTitle>Cost model (Claude API)</CardTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12 }}>
          {[
            { label: 'Model', value: 'claude-haiku-4-5', note: 'pipeline verify' },
            { label: 'Input price', value: '$0.80 / 1M tokens', note: 'estimated 70% of usage' },
            { label: 'Output price', value: '$4.00 / 1M tokens', note: 'estimated 30% of usage' },
            { label: 'Estimated all-time', value: loading ? '\u2026' : '$' + (stats?.estCost || 0).toFixed(4), note: 'rough estimate' },
          ].map(({ label, value, note }) => (
            <div key={label} style={{ padding: '12px 14px', background: '#f8fafb', borderRadius: 8 }}>
              <div style={{ fontSize: '.65rem', color: '#9eb8c8', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: '.82rem', fontWeight: 600, color: '#0f1e28' }}>{value}</div>
              {note && <div style={{ fontSize: '.6rem', color: '#b0c8d4', marginTop: 2 }}>{note}</div>}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, padding: '8px 12px', background: '#f0f7fc',
          border: '.5px solid #b6d4e8', borderRadius: 8, fontSize: '.7rem', color: '#185FA5' }}>
          Cost estimates are based on sana_invocations.tokens_used and current Claude API pricing.
          Actual costs may vary based on exact token split and model version.
        </div>
      </Card>
    </div>
  );
}
